import React, { useEffect, useRef, useId } from "react";
import "./css/ConfirmModal.css";

export default function ConfirmModal({
  open,
  title = "Are you sure?",
  message = "Please confirm this action.",
  confirmText = "Yes",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  busy = false,
  closeOnOverlayClick = true,   // new: disable overlay close if you want
  initialFocus = "confirm",     // "confirm" | "cancel"
}) {
  const dialogRef = useRef(null);
  const confirmRef = useRef(null);
  const cancelRef = useRef(null);
  const prevFocusRef = useRef(null);

  const titleId = useId();
  const messageId = useId();

  // Focus management: trap focus, restore focus on close
  useEffect(() => {
    if (!open) return;

    // store previously focused element
    prevFocusRef.current = document.activeElement;

    // autofocus
    const target =
      initialFocus === "cancel" ? cancelRef.current : confirmRef.current;
    (target || dialogRef.current)?.focus?.();

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (!busy) onCancel?.();
        return;
      }
      if (e.key === "Tab") {
        // focus trap
        const focusables = dialogRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const list = Array.from(focusables || []).filter(
          (el) => !el.hasAttribute("disabled") && el.tabIndex !== -1
        );
        if (list.length === 0) return;

        const first = list[0];
        const last = list[list.length - 1];

        if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        } else if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      // restore focus
      const prev = prevFocusRef.current;
      if (prev && typeof prev.focus === "function") {
        prev.focus();
      }
    };
  }, [open, busy, onCancel, initialFocus]);

  if (!open) return null;

  const stop = (e) => e.stopPropagation();

  const onOverlayClick = () => {
    if (!busy && closeOnOverlayClick) onCancel?.();
  };

  return (
    <div
      className="cm-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={messageId}
      onClick={onOverlayClick}
    >
      <div
        className="cm-card"
        ref={dialogRef}
        onClick={stop}
        tabIndex={-1} // needed so the div can receive initial focus
      >
        <div className="cm-head">
          <h3 className="cm-title" id={titleId}>{title}</h3>
          <button
            className="cm-close"
            onClick={onCancel}
            aria-label="Close"
            type="button"
            disabled={busy}
          >
            ✕
          </button>
        </div>

        <div className="cm-body">
          <p className="cm-message" id={messageId}>{message}</p>
        </div>

        <div className="cm-foot">
          <button
            className="cm-btn"
            onClick={onCancel}
            disabled={busy}
            type="button"
            ref={cancelRef}
          >
            {cancelText}
          </button>
          <button
            className="cm-btn cm-btn--primary"
            onClick={onConfirm}
            disabled={busy}
            type="button"
            ref={confirmRef}
          >
            {busy ? "Working…" : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
